import type { CatalogReport } from './DepartmentCatalog';
import { catalogHref } from '../lib/warehouse-preview-route';

// Screen names/order from Richard's HTML 2; never an authorization catalog.
export const FINANCE_SCREENS = [
  { title: 'Financial Statements', reports: ['income-statement', 'balance-sheet'] },
  { title: 'Recurring Revenue', reports: [] },
  { title: 'Revenue', reports: [] },
  { title: 'AR Aging', reports: ['ar-aging'] },
  { title: 'AP Aging', reports: [] },
  { title: 'Collections & Dunning', reports: [] },
  { title: 'Deferred Revenue Waterfall', reports: ['deferred-waterfall'] },
  { title: 'Fixed Asset Roll-forward', reports: ['fixed-assets'] },
  { title: 'Vendor & Expense Analysis', reports: [] },
  { title: 'Department P&L', reports: ['department-income-statement'] },
  { title: 'Source & Reconciliation', reports: [] },
] as const;

export function financeScreens(reports: readonly CatalogReport[]) {
  const allowed = new Set(reports.map((report) => report.id));
  return FINANCE_SCREENS.map((screen) => ({
    title: screen.title,
    reportIds: screen.reports.filter((id) => allowed.has(id)),
    // An AR capability does not confer collections access; likewise GL actuals
    // does not manufacture permission for department P&L or reconciliation.
    reason: screen.reports.length ? 'Not in your authorized report catalog' : 'Report adapter not implemented',
  }));
}

export default function FinanceNavigation({ reports, selectedReport, variant = 'rail' }: {
  reports: readonly CatalogReport[]; selectedReport?: string | null; variant?: 'rail' | 'cards';
}) {
  return <div className={`fn-screens fn-${variant}`} aria-label="Finance screen navigation">
    {financeScreens(reports).map((screen) => screen.reportIds.length
      ? <a key={screen.title} href={`/${catalogHref('finance', screen.reportIds[0])}`}
          aria-current={screen.reportIds.some((id) => id === selectedReport) ? 'page' : undefined}>
          <span>{screen.title}</span><small>{screen.reportIds.some(id => ['income-statement', 'balance-sheet', 'department-income-statement', 'ar-aging'].includes(id)) ? 'Open authorized report' : 'API adapter, screen pending'}</small>
        </a>
      : <div className="fn-unavailable" key={screen.title}>
          <button type="button" disabled aria-describedby={`fn-${variant}-${screen.title.replace(/[^a-z]/gi, '')}`}>
            {screen.title}
          </button>
          <small id={`fn-${variant}-${screen.title.replace(/[^a-z]/gi, '')}`}>{screen.reason}</small>
        </div>)}
    {reports.some((report) => report.id === 'actuals') && <a href={`/${catalogHref('finance', 'actuals')}`}
      aria-current={selectedReport === 'actuals' ? 'page' : undefined}>
      <span>GL actuals</span><small>Supporting API adapter · screen pending</small>
    </a>}
  </div>;
}

export function StatementNavigation({ reports, selectedReport }: { reports: readonly CatalogReport[]; selectedReport: string }) {
  const allowed = new Set(reports.map((report) => report.id));
  return <nav className="fn-statements" aria-label="Financial statement selection">
    {[['income-statement', 'P&L'], ['balance-sheet', 'Balance Sheet'], ['cash-flow', 'Cash Flow']].map(([id, label]) =>
      id !== 'cash-flow' && allowed.has(id)
        ? <a key={id} href={`/${catalogHref('finance', id)}`} aria-current={id === selectedReport ? 'page' : undefined}>{label}</a>
        : <span key={id}><button type="button" disabled>{label}</button>
          <small>{id === 'cash-flow' ? 'Adapter not implemented' : 'Not in your authorized catalog'}</small></span>)}
  </nav>;
}
